// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## chord.js
//
// An object representing chords in music theory.
//   To change an existing chord, just set the root, quality, and inversion
//   properties directly, or call chord.set(root, quality, inversion).
//
//   chord.root : the note for which the chord is named (uTheory.Notation.Note)
//
//   chord.quality : the quality of the chord, can be set many ways,
//      but will always return the shortest version (symbol version):
//
//      triads: (full name, abbreviation, symbol)
//        Major, Maj, M
//        minor, min, m
//        Augmented, Aug, A
//        diminished, dim, d
//
//      seventh chords:
//        Major 7, Maj7, M7
//        minor 7, min7, m7
//        Dominant 7, Dom7, 7
//        half diminished 7, half-dim7, o/7
//        diminished 7, dim7, o7
//
//  chord.inversion : the inversion of the chord
//      0, 1, 2, 3
//
//  chord.figures : the figured bass for that inversion:
//      Triads: "", 63, 64
//      Seventh Chords: 7, 65, 43, 42
//
//  Examples:
//    var chord = new uTheory.Notation.Chord("Bb3", "Dominant 7");
//    chord.quality; // "7"
//    chord.root; // "Bb3"
//    chord.seventh; // "Ab4"
//    chord.root = "D";
//    chord.seventh; // "C5"
//    chord.bass; // "D4"
//    chord.inversion++;
//    chord.bass; // "F#4"
//    chord.quality = "M";
//    chord.seventh; // undefined
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type {
  Figure,
  ChordQualityName,
  InversionNumber,
  ValidFigure,
} from 'shared/definitions/musicTheory/chords';
import { chordHalfStepsFromRoot } from 'shared/definitions/musicTheory/chords';
import { chordQualitiesByScaleDegree } from 'shared/definitions/musicTheory/chords';
import type { NoteName, ShortNoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { scaleDegrees } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import { uniqueFilter } from 'shared/src/utils/uniqueFilter';
import { arrayContentsEqual } from 'shared/src/utils/arrayContentsEqual';
import { sum } from 'shared/src/utils/sum';
import NoteHolder from './noteHolder';
import Note from './note';
import KeySignature from './keySignature';
import * as Font from '../uTheory.Font/font';
import utils from '../utils';
import type MusicObject from './musicObject';
import type { MidiInfoObject, MidiMessage } from './soundBufferHolder';

const debug = false;
const log = (...args: any[]) => debug && console.log(...args);

export interface ChordHTMLOptions {
  quality?: 'full' | 'abbreviation' | 'symbol' | false;
  inversion?: 'full' | 'abbreviation' | 'figures' | false;
}

const allChords: `${ShortNoteName} ${ChordQualityName}`[] = [];
let chordsByQuality: Record<ChordQualityName, `${ShortNoteName} ${ChordQualityName}`[]>;

export default class Chord implements MusicObject {
  chordParts: { root: Note; third: Note; fifth: Note; seventh?: Note };

  noteHolder: NoteHolder = new NoteHolder();

  isSeventh = false;

  private _quality: ChordQualityName;

  private _inversion: InversionNumber;

  // Public constructor
  constructor();
  constructor(chordString: string);
  constructor(root: NoteName | Note, quality?: string);
  constructor(root?: NoteName | Note, quality?: string, inversion?: InversionNumber | ValidFigure);
  constructor(root?: string | Note, quality?: string, inversion?: InversionNumber | ValidFigure) {
    // If only one arg, it may be a chord name string like G#Dom7:
    if (root && !quality && !inversion) {
      this.setFromString(root);
      return;
    }
    root = root || 'C4';
    quality = quality || 'Major';
    inversion = inversion || 0;

    this.set(root as NoteName, quality, inversion);
  }

  setFromString(string) {
    string = string.trim();

    // Grab and eliminate any inversion at end of string:
    const figures =
      ['53', '63', '6', '64', '753', '7', '653', '65', '643', '43', '642', '42'].find(
        (figure) => string.slice(-figure.length) === figure
      ) || '';

    string = string.slice(0, -figures.length || string.length);

    // Grep as far as you can to get the note:
    const rootArray = /[ABCDEFG][#bxv]{0,2}[/]{0,1}\d{0,1}/.exec(string);
    if (!rootArray) {
      throw new Error('When initializing a chord with only one argument, provide a note name.');
    }
    const root = rootArray[0];

    string = string.slice(root.length).trim();

    // Let's guess our quality:
    const seventh = Chord.figuresAreSeventh(figures) ? '7' : '';
    const { quality } = Chord.normalizeQuality(string + seventh);

    const inversion = Chord.figuresToInversion(figures);
    this.set(root as NoteName, quality, inversion);
  }

  /*  ================================================================
   *   Getter & Setter Functions:
   *     For adjusting the root, quality, and inversion of the chord
   *
   *  ================================================================
   */

  set(root?: NoteName | Note, quality?: string, inversion?: ValidFigure | InversionNumber) {
    if (typeof root !== 'undefined') this._setRoot(root);
    if (typeof quality !== 'undefined') this._setQuality(quality);
    if (typeof inversion !== 'undefined') this._setInversion(inversion);
    this._updateChordParts();
    return this;
  }

  get root(): Note {
    return this.chordParts.root;
  }

  setRoot(note: Note | NoteName) {
    this._setRoot(note);
    this._updateChordParts();
  }

  get quality(): ChordQualityName {
    return this._quality;
  }

  setQuality(quality: string) {
    this._setQuality(quality);
    this._updateChordParts();
  }

  get inversion() {
    return this._inversion;
  }

  setInversion(inversionOrFigures: InversionNumber | ValidFigure) {
    this._setInversion(inversionOrFigures);
    this._updateChordParts();
  }

  get third() {
    return this.chordParts.third;
  }

  get fifth() {
    return this.chordParts.fifth;
  }

  get seventh() {
    return this.chordParts.seventh;
  }

  get bass() {
    return this.noteHolder.notes[0];
  }

  setFigures(figures: InversionNumber | ValidFigure) {
    this._setInversion(figures);
    this._updateChordParts();
  }

  get figures(): Figure {
    if (!this.isSeventh) {
      return ['', '63', '64'][this.inversion] as Figure;
    }
    return ['7', '65', '43', '42'][this.inversion] as Figure;
  }

  get octave() {
    return this.root.octave;
  }

  setOctave(octave: number) {
    const root = this.root.clone();
    root.octave = octave;
    this.setRoot(root);
  }

  /*
   *  ================================================================
   *   Comparison Methods:
   *     For comparing to other chords or to NoteHolders
   *
   *  ================================================================
   */

  // NoteHolders that evaluate to the same chord are accepted to be
  // true -- in other words, octaves don't mater for NoteHolders.
  equals(chordNoteHolderOrString) {
    if (utils.isString(chordNoteHolderOrString)) {
      chordNoteHolderOrString = new Chord(chordNoteHolderOrString);
    }
    const that = chordNoteHolderOrString;
    if (chordNoteHolderOrString instanceof Chord) {
      return (
        this._inversion === that._inversion &&
        this._quality === that._quality &&
        this.root.equals(that.root)
      );
    }

    if (chordNoteHolderOrString instanceof NoteHolder) {
      const nh = chordNoteHolderOrString.clone().sort();
      return (
        nh.equalsIgnoreOctave(this.noteHolder) &&
        nh.notes[0].equalsIgnoreOctave(this.noteHolder.notes[0])
      );
    }
    // I don't know how to compare that type... couldn't possibly be
    // a representation of a chord!
    return false;
  }

  equalsIgnoreOctave(chordNoteHolderOrString) {
    if (utils.isString(chordNoteHolderOrString)) {
      chordNoteHolderOrString = new Chord(chordNoteHolderOrString);
    }
    const that = chordNoteHolderOrString;
    if (chordNoteHolderOrString instanceof Chord) {
      return (
        this._inversion === that._inversion &&
        this._quality === that._quality &&
        this.root.equalsIgnoreOctave(that.root)
      );
    }
    if (chordNoteHolderOrString instanceof NoteHolder) {
      return this.equals(chordNoteHolderOrString);
    }
    // I don't know how to compare that type... couldn't possibly be
    // a representation of a chord!
    return false;
  }

  equalsIgnoreInversion(that: Chord | NoteHolder | string): boolean {
    if (utils.isString(that)) {
      that = new Chord(that);
    }

    if (that instanceof Chord) {
      return this._quality === that._quality && this.root === that.root;
    }
    if (that instanceof NoteHolder) {
      return that.equalsIgnoreOctave(this.noteHolder);
    }
    // I don't know how to compare that type... couldn't possibly be
    // a representation of a chord!
    return false;
  }

  /*
   *  ================== Analysis Methods ============================
   *
   *     Generate chords from noteHolders, analyze quality, etc...
   *
   *  ================================================================
   */

  /**
   * @param {Note} note
   * @returns {string|undefined} 'root', 'third', 'fifth', 'seventh', undefined
   */
  getFactorFromNote(note) {
    note = Note.stringToNote(note);
    if (!(note instanceof Note))
      throw new Error('Must provide note or string to determine factor.');

    return ['root', 'third', 'fifth', 'seventh'].find((part) =>
      this[part] ? this[part].equalsIgnoreOctave(note) : false
    );
  }

  /**
   * Returns a noteHolder in openSpacing preserving the inversion of the chord,
   * unless the options anyInversion is set to true
   * @param {boolean} anyInversion default false
   * @returns {NoteHolder}
   */
  getOpenSpacing(anyInversion = false) {
    const noteHolder = this.noteHolder.clone();

    // If needed, add a 4th part:
    // root or fifth for most triads, but always 3rd for diminished:
    if (noteHolder.length !== 4) {
      if (this.quality === 'o') noteHolder.push(this.third.clone());
      else {
        noteHolder.push(Math.random() >= 0.5 ? this.root.clone() : this.fifth.clone());
      }
    }

    // Shuffle the notes, preserve the inversion if needed:
    const notes = anyInversion
      ? utils.shuffle(noteHolder.notes)
      : [noteHolder.notes[0]].concat(utils.shuffle(noteHolder.notes.slice(1)));

    // Put the bass in his lowest octave:
    notes[0].octave = 1;
    while (notes[0].isLessThan('E2')) notes[0].octave++;

    // Compact these down, with no repetition of notes in same octave:
    notes.reduce((lower, higher) => {
      higher.octave = lower.octave;
      while (higher.isLessThanOrEquals(lower)) higher.octave++;
      return higher;
    });

    // If there's already more than a 4th between TA or AS it's already open:
    const alreadyOpen =
      notes[1].getGenericIntervalTo(notes[2]) > 4 || notes[2].getGenericIntervalTo(notes[3]) > 4;

    // Otherwise, flip the alto up an octave, to make her the soprano:
    if (!alreadyOpen) {
      notes[2].octave++;
      // Sort the note holder indexes:
    }
    noteHolder.sort();

    function rangeTest(holder) {
      const ranges = [
        ['C4', 'A5'],
        ['G3', 'D5'],
        ['C3', 'G4'],
        ['E2', 'D4'],
      ].reverse();
      const outOfRange = ranges.some(
        (range, index) =>
          holder.notes[index].isLessThan(range[0]) || holder.notes[index].isGreaterThan(range[1])
      );
      return !outOfRange;
    }

    // Now go up octaves in the lower 3 voices until we can't:
    const potentialHolders = [0, 1, 2, 3]
      .map((displacement) => {
        const holder = noteHolder.clone();
        holder.notes[1].octave += displacement;
        holder.notes[2].octave += displacement;
        holder.notes[3].octave += displacement;
        return holder;
      })
      .filter(rangeTest);
    if (potentialHolders.length > 2) potentialHolders.reverse().length--;
    const finalHolder = potentialHolders[
      Math.floor(Math.random() * potentialHolders.length)
    ] as NoteHolder;
    // Perhaps the bass could come up an octave -- but only if it doesn't
    // wind up being closer to the tenor than the average spacing:
    const finalNotes = finalHolder.notes;

    if (finalNotes[0].getGenericIntervalTo(finalNotes[1]) - 7 >= 3) {
      if (Math.random() >= 0.5) finalNotes[0].octave++;
    }

    return finalHolder;
  }

  getMidiMessages(midiInfo?: MidiInfoObject | undefined): MidiMessage[] {
    return this.noteHolder.getMidiMessages(midiInfo);
  }

  /*
   *  =================== String Output Methods ======================
   *
   *     For displaying the names of the chord in various ways.
   *
   *  ================================================================
   */

  /* @param options {
      root: "long" (Bb4), "short" (Bb), false (omit)
      quality: "full" (Half-diminished),
        "abbreviation" (half-dim), -- DEFAULT
        "symbol" (o/)
        false (omit)
      inversion: "full" (First inversion),
        "abbreviation" ("1st inv.),
        "figures" (6) -- DEFAULT,
        false (don't display it)
      accidentalFont: bool default false
    }
  */
  toString(
    options?: {
      root?: 'short' | 'long' | false;
      quality?: 'full' | 'abbreviation' | 'symbol' | false;
      inversion?: 'full' | 'abbreviation' | 'figures' | false;
      accidentalFont?: boolean;
    },
    toAccidentalFont?
  ) {
    // GCR To-Do: Refactor anything that uses it this way:
    const nameTypes = ['full', 'abbreviation', 'symbol'];
    if (utils.isInteger(options)) {
      console.warn('Chord.toString(int) is deprecated.');
      options = {
        quality: ['full', 'abbreviation', 'symbol'][options as number] as any,
      };
    }

    options = {
      root: 'short',
      quality: 'abbreviation',
      inversion: 'figures',
      accidentalFont: toAccidentalFont || false,
      ...options,
    };

    let root;
    switch (options.root) {
      case 'long':
        root = options.accidentalFont ? this.root.toAccidentalFontString() : this.root.toString();
        break;
      case false:
        root = '';
        break;
      default:
        root = this.root.toShortString(options.accidentalFont);
        break;
    }

    let quality = options.quality
      ? this.getQualityString(nameTypes.indexOf(options.quality), options.accidentalFont, false)
      : '';

    if (options.inversion !== 'figures' && this.isSeventh) {
      quality += options.accidentalFont ? Font.toHTML('figuredBass', '7') : 7;
    }

    const figures = options.accidentalFont ? this.figuresToFont() : this.figures;

    let inversion = options.inversion ? ` ${this.getInversionString(options.inversion)}` : '';
    inversion = options.inversion === 'figures' ? figures : inversion;

    let str = root + (root && quality ? ' ' : '') + quality;

    if (str && options.inversion && options.inversion !== 'figures') {
      str += ' ';
    }
    str += inversion;

    return str;
  }

  toHTML({ inversion = 'figures', quality = 'abbreviation' }: ChordHTMLOptions = {}): string {
    const rootString = this.root.toAccidentalFontShortString();
    const spaceAfterRoot = quality === 'full' || quality === 'abbreviation' ? ' ' : '';
    const includeSevenInQuality = quality && inversion !== 'figures' && this.isSeventh;
    let qualityString = quality ? this.getQualityString(quality, false, false) : '';
    if (quality === 'abbreviation' || quality === 'symbol') {
      qualityString = Font.toHTML('quality', qualityString, { ariaSpaceBefore: true });
    }
    if (includeSevenInQuality) {
      qualityString += Font.toHTML('figuredBass', '7');
    }
    const spaceBeforeInversion = inversion && inversion !== 'figures' ? ' ' : '';
    const inversionString = this.getInversionString(inversion, true);
    return `${rootString}${spaceAfterRoot}${qualityString}${spaceBeforeInversion}${inversionString}`;
  }

  getQualityString(
    fullAbbrevOrSymbol: 'full' | 'abbreviation' | 'symbol' | number,
    accidentalFont?: boolean,
    include7th?: boolean
  ) {
    if (utils.isString(fullAbbrevOrSymbol)) {
      fullAbbrevOrSymbol = ['full', 'abbreviation', 'symbol'].indexOf(fullAbbrevOrSymbol);
    }

    return this.qualityToFont(this.quality, fullAbbrevOrSymbol, accidentalFont, include7th);
  }

  qualityToFont(quality, nameType, accidentalFont, include7th) {
    // Nametype: 0--Full, 1--abbrev, 2--symbol
    const fontQuality = Chord.chordQualityNames[quality][nameType];
    quality = accidentalFont && nameType === 2 ? Font.toHTML('quality', fontQuality) : fontQuality;
    if (include7th && this.isSeventh) {
      quality += Font.toHTML('figuredBass', '7');
    }
    return quality;
  }

  getInversionString(
    fullAbbrevOrFigures: 'full' | 'abbreviation' | 'symbol' | 'figures' | false = 'full',
    accidentalFont?: boolean
  ) {
    if (!fullAbbrevOrFigures) return '';
    if (fullAbbrevOrFigures === 'figures') {
      return accidentalFont ? this.figuresToFont() : this.figures;
    }

    if (['full', 'abbreviation', 'symbol'].indexOf(fullAbbrevOrFigures) === -1) {
      throw new Error(
        `fullAbbrevOrFigures must be 'full', 'abbreviation', 'symbol' or 'figures', not ${fullAbbrevOrFigures}`
      );
    }

    const inversionStrings = {
      full: ['root position', 'first inversion', 'second inversion', 'third inversion'],
      abbreviation: ["root pos'n", 'first inv.', 'second inv.', 'third inv.'],
      symbol: ["root pos'n", '1st inv.', '2nd inv.', '3rd inv.'],
    };

    fullAbbrevOrFigures = fullAbbrevOrFigures || 'full';
    return inversionStrings[fullAbbrevOrFigures][this.inversion];
  }

  figuresToFont(figures?: Figure): string {
    log('ftf');
    figures = typeof figures !== 'undefined' ? figures : this.figures;
    return Font.toHTML('figuredBass', figures);
  }

  /*  =========== "Private" Functions ============ */

  private _setRoot(note: NoteName | Note) {
    this.chordParts = {
      root: Note.stringToNote(note),
      third: undefined,
      fifth: undefined,
      seventh: undefined,
    };
  }

  private _setQuality(quality) {
    const normalizedQuality = Chord.normalizeQuality(quality);
    this.isSeventh = normalizedQuality.isSeventh;
    this._quality = normalizedQuality.quality;
  }

  private _setInversion(inversion: InversionNumber | ValidFigure) {
    const wasSeventh = this.isSeventh;
    let nowSeventh = this.isSeventh;
    let inversionNumber: InversionNumber;

    switch (Chord.numberIsFiguresOrInversionNumber(inversion)) {
      case 'figures':
        nowSeventh = Chord.figuresAreSeventh(inversion);
        inversionNumber = Chord.figuresToInversion(inversion);
        break;
      case 'inversion':
        inversionNumber = Number(inversion as any) as any;
        break;
      case false:
        throw new Error(
          `Invalid Inversion: Must be a number or string from 0-3 or figures, not ${inversion}`
        );
    }

    if (inversionNumber === 3) nowSeventh = true;

    // Check to be sure inversion within bounds -- mod it up or down if
    // needed:
    const maxInversion = nowSeventh ? 3 : 2;
    if (inversionNumber > maxInversion) inversionNumber %= maxInversion;

    if (wasSeventh !== nowSeventh) {
      if (nowSeventh) {
        this.isSeventh = true;
        switch (this._quality) {
          case 'M':
            this._quality = '7';
            break;
          case 'm':
            this._quality = 'm7';
            break;
          case 'A':
            throw new Error('NaQ: Cannot have an Aug seventh.');
          case 'o':
            this._quality = 'o7';
            break;
          default:
            throw new Error(`${this._quality} cannot be made a seventh.`);
        }
      } else {
        this.isSeventh = false;
        switch (this._quality) {
          case 'M7':
          case '7':
            this._quality = 'M';
            break;
          case 'm7':
            this._quality = 'm';
            break;
          case 'o7':
          case 'o/7':
            this._quality = 'o';
            break;
          default:
            throw new Error(`${this._quality} cannot be made a triad.`);
        }
      }
    }

    this._inversion = inversionNumber;
  }

  private _updateChordParts() {
    const intervals = Chord.chordIntervals[this._quality];
    this.noteHolder.length = 0;
    this.noteHolder.push(this.chordParts.root);
    intervals.forEach((interval) => {
      const note = this.chordParts.root.getNoteAt(interval);
      if (note) {
        this.noteHolder.push(note);
      } else {
        throw new Error(
          `${this.toString()} requires a double or triple accidental, which is not supported by uTheory at this point.`
        );
      }
    });

    const n = this.noteHolder.notes;

    this.chordParts.root = n[0];
    this.chordParts.third = n[1];
    this.chordParts.fifth = n[2];
    this.chordParts.seventh = n[3];

    const oldRootOctave = n[0].octave;

    for (let i = 0; i < this._inversion; i++) {
      n[i].octave++;
    }
    if (n[0].octave !== oldRootOctave) {
      n.forEach((note) => {
        note.octave--;
      });
    }

    this.noteHolder.sort();
  }

  static normalizeQuality(quality: string) {
    if (typeof quality === 'undefined') quality = 'M';
    quality = `${quality}`;
    let normalizedQuality: ChordQualityName | null = null;

    // Is it a seventh chord?
    let isSeventh = quality.search('7') !== -1;
    const seventh = isSeventh ? '7' : '';

    if (quality === '') {
      normalizedQuality = 'M';
    }

    if (quality.search('M') === 0) {
      normalizedQuality = `M${seventh}`;
    }

    if (quality.search('m') === 0) {
      normalizedQuality = `m${seventh}`;
    }

    if (quality.search('A') === 0 || quality.search(/\+/) !== -1) {
      normalizedQuality = 'A';
    }

    if (quality.search('d') === 0 || quality.search('o7') !== -1 || quality.search('o') === 0) {
      normalizedQuality = `o${seventh}`;
    }

    if (quality.search('h') === 0 || quality.search('o/7') !== -1) {
      normalizedQuality = 'o/7';
      isSeventh = true;
    }

    if (quality.search('D') === 0 || quality.search('7') === 0) {
      normalizedQuality = '7';
      isSeventh = true;
    }

    // If we didn't find a matching quality string, but it is a 7th,
    // we assume its a dominant seventh:
    if (isSeventh && !normalizedQuality) {
      normalizedQuality = '7';
    }

    // If we didn't find a matching quality string, and its a triad,
    // we assume its a major triad:
    if (!isSeventh && !normalizedQuality) {
      normalizedQuality = 'M';
    }

    return { quality: normalizedQuality, isSeventh };
  }

  // ============== Tables ===============

  static chordIntervals: { [quality: string]: IntervalName[] } = {
    M: ['M3', 'P5'],
    m: ['m3', 'P5'],
    A: ['M3', 'A5'],
    o: ['m3', 'd5'],
    M7: ['M3', 'P5', 'M7'],
    m7: ['m3', 'P5', 'm7'],
    7: ['M3', 'P5', 'm7'],
    o7: ['m3', 'd5', 'd7'],
    'o/7': ['m3', 'd5', 'm7'],
  };

  static triadQualityNames = {
    // For converting to string names...
    //      triads: (full name, abbreviation, symbol)
    M: ['Major', 'Maj', 'M', 'M'],
    m: ['minor', 'min', 'm', 'm'],
    A: ['Augmented', 'Aug', '+'],
    o: ['diminished', 'dim', 'o'],
  };

  static seventhQualityNames = {
    //      seventh chords:
    //      note: we leave off 7 in case of inversions
    M7: ['Major', 'Maj', 'M'],
    m7: ['minor', 'min', 'm'],
    7: ['Dominant', 'Dom', ''],
    o7: ['diminished', 'dim', 'o'],
    'o/7': ['half-diminished', 'half-dim', 'o/'],
  };

  static chordQualityNames = utils.merge(Chord.triadQualityNames, Chord.seventhQualityNames);

  static triadFigures = [
    // 0
    ['', '53', '5'],
    // 1
    ['6', '63'],
    // 2
    ['64'],
  ];

  static seventhFigures = [
    // 0
    ['7', '753', '73', '75'],
    // 1
    ['65', '653'],
    // 2
    ['43', '643'],
    // 3
    ['42', '642'],
  ];

  /* qualityEquals
   * @param a, b: two quality strings
   * returns bool
   */
  static qualityEquals = (a, b) => {
    const qA = Chord.normalizeQuality(a);
    const qB = Chord.normalizeQuality(b);
    return qA.quality === qB.quality && qA.isSeventh === qB.isSeventh;
  };

  /**
   * Convert an integer inversion to string figures
   * @param {number} inversion
   * @param {boolean} isSeventh
   * @returns {string} figures
   */
  static inversionToFigures = (inversion, isSeventh): Figure => {
    const table = isSeventh ? Chord.seventhFigures : Chord.triadFigures;
    if (typeof table[inversion] !== 'undefined') return table[inversion][0] as Figure;
    throw new Error(
      `Not an Inversion: ${inversion} is not a valid inversion for a ${
        isSeventh ? 'seventh chord.' : 'triad.'
      }`
    );
  };

  /**
   * Convert a string figured bass to an integer inversion.
   * @param {string} figures
   * @returns {number} inversion number
   */
  static figuresToInversion = (figures): InversionNumber => {
    figures = `${figures}`;
    let i;
    let table = Chord.triadFigures;
    for (i = 0; i < table.length; i++) {
      if (table[i].indexOf(figures) !== -1) return i;
    }
    table = Chord.seventhFigures;
    for (i = 0; i < table.length; i++) {
      if (table[i].indexOf(figures) !== -1) return i;
    }
    throw new Error(`NAF: ${figures} is not a valid set of figured bass symbols.`);
  };

  static figuresAreSeventh = (figures) => {
    const table = Chord.seventhFigures;
    for (let i = 0; i < table.length; i++) {
      if (table[i].indexOf(figures) !== -1) return true;
    }
    return false;
  };

  static isValidFigures = (figures): figures is Figure => {
    try {
      Chord.figuresToInversion(figures);
    } catch (err) {
      return false;
    }
    return true;
  };

  static isValidInversion(inversion): inversion is InversionNumber {
    return [0, 1, 2, 3].indexOf(inversion) !== -1;
  }

  static numberIsFiguresOrInversionNumber = (number) => {
    if (typeof number === 'undefined') number = '';
    if ([0, 1, 2, 3].indexOf(Number(number)) !== -1) return 'inversion';
    if (
      [
        '',
        '53',
        '63',
        '6',
        '64',
        '7',
        '753',
        '75',
        '73',
        '653',
        '65',
        '643',
        '43',
        '642',
        '42',
      ].indexOf(`${number}`) !== -1
    )
      return 'figures';
    return false;
  };

  /**
   * Returns a possible chord for the given midis. If no chord is possible,
   * returns false.
   * @param midiNumbers
   * @param options
   * @returns
   */
  static fromMidis = (midiNumbers: number[], { allowIncomplete = true } = {}) => {
    // console.log('*** fromMidis ***');
    // Compress all midi numbers into one octave, and filter any repeated notes:
    const compressedMidis = midiNumbers
      .map((n) => n % 12)
      .filter(uniqueFilter)
      .sort((a, b) => a - b);

    // A chord must have at least two unique notes:
    if (compressedMidis.length < 2) return false;

    // A chord can have no more than 4 unique notes:
    if (compressedMidis.length > 4) return false;

    // console.log('compressedMidis:', compressedMidis);

    // Map to arrays of each possible rotation:
    const inversions = compressedMidis.map((root) =>
      compressedMidis
        .map((midi) => (midi < root ? midi + 12 : midi))
        // Sort them
        .sort((a, b) => a - b)
        // And filter any P5's if allowIncomplete is true
        .filter((midi, _, inversionMidis) =>
          allowIncomplete ? midi - inversionMidis[0]! !== 7 : true
        )
    );

    const chordsArray = Object.keys(chordHalfStepsFromRoot).map((key) => ({
      quality: key,
      halfSteps: chordHalfStepsFromRoot[key as keyof typeof chordHalfStepsFromRoot]!,
    }));

    log(inversions);

    // Find an inversion that matches a possible arrangement of chordal half steps:
    const matchedChords = inversions
      .map((inversionMidis) => {
        const rootMidi = inversionMidis[0]!;
        const matchedQuality = chordsArray.find(({ halfSteps }) => {
          const requiredMidis = halfSteps
            .map((n) => n)
            // If allowIncomplete is set, remove the perfect fifth from half steps
            .filter((n) => (allowIncomplete ? n !== 7 : true))
            // And add the rootMidi value to each half step
            .map((n) => n + rootMidi);

          return arrayContentsEqual(requiredMidis, inversionMidis);
        });

        // If we didn't find a matched quality, this is not a chord:
        if (!matchedQuality) return false;
        const chords =
          // Get all the root notes:
          Note.fromMidiAllSpellings(rootMidi)
            // Get all the possible chords on this root
            .map((root) => {
              let chord: Chord | undefined;
              try {
                chord = new Chord(root, matchedQuality.quality);
              } catch (_) {
                // Ignore -- some spelling will be invalid, we expect this.
              }
              return chord;
            })
            .filter((c): c is Chord => !!c);

        return chords;
      })
      .filter((c): c is Chord[] => !!c)
      .flat();

    if (!matchedChords.length) return false;

    // Now find the simplest spelling of the chord:
    const simplestChord = matchedChords
      // Count the accidentals in each chord:
      .map((chord) => ({
        chord,
        acc: chord.noteHolder.notes.map((n) => Math.abs(n.getAccidentalInt())).reduce(sum, 0),
      }))
      // Sort by fewest, and return that one
      .sort((a, b) => a.acc - b.acc)[0]!.chord;

    // Finally, let's set the inversion:
    const bass = Note.fromMidi(midiNumbers.slice(0).sort()[0]!);

    if (simplestChord.third.equalsEnharmonicallyIgnoreOctave(bass)) {
      simplestChord.setInversion(1);
    } else if (simplestChord.fifth.equalsEnharmonicallyIgnoreOctave(bass)) {
      simplestChord.setInversion(2);
    } else if (simplestChord.seventh?.equalsEnharmonicallyIgnoreOctave(bass)) {
      simplestChord.setInversion(3);
    }

    // Set the octave to 4, just for ease.
    simplestChord.setOctave(4);

    return simplestChord;
  };

  static fromNoteHolder = (noteHolder: NoteHolder, allowIncomplete = false) => {
    if (noteHolder instanceof Chord) return noteHolder;
    const nh = noteHolder.clone();
    nh.sortAndCompact();
    log(`Trying to make chord from ${nh}`);

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const isRootPosition = (nh) => {
      for (let i = 1; i < nh.length; i++) {
        const interval = nh.notes[0].getGenericIntervalTo(nh.notes[i]);
        log(`genericInterval: ${interval}`);
        if (interval !== 3 && interval !== 5 && interval !== 7) return false;
      }
      return true;
    };

    // Find root:
    let rotation = 0;
    const maxRotation = nh.length;
    let validStructure = isRootPosition(nh);

    // Rotate into root position if needed:
    while (!validStructure && rotation < maxRotation) {
      nh.notes[0].octave++;
      nh.sort();
      rotation++;
      validStructure = isRootPosition(nh);
    }

    // If we haven't found one, then there isn't a possible root position:
    log(`validStructure: ${validStructure}`);
    if (!validStructure) return false;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const qualityMatch = (nh, intervals) => {
      // Make a noteHolder based on the intervals:
      const chordHolder = new NoteHolder(nh.notes[0]);
      const root = nh.notes[0];
      for (let i = 0; i < intervals.length; i++) {
        const newNote = root.getNoteAt(intervals[i]);
        if (newNote !== false) {
          chordHolder.push(newNote);
        } else {
          return false;
        }
      }
      let match = nh.equals(chordHolder);
      if (!match && allowIncomplete && intervals.indexOf('P5') !== -1) {
        chordHolder.remove(root.getNoteAt('P5'));
        match = nh.equals(chordHolder);
      }
      return match;
    };

    const chord = new Chord();
    const quality = Object.keys(Chord.chordIntervals).find((key) => {
      const ints = Chord.chordIntervals[key];
      return qualityMatch(nh, ints);
    });

    log('quality?', quality);
    // If we didn't find one, it doesn't exist:
    if (!quality) return false;

    chord.set(nh.notes[0], quality);

    // Determine the inversion and return the chord:
    const exists = chord.noteHolder.noteExistsAtIgnoreOctave(noteHolder.sort().notes[0]);
    if (!exists) return false; // this should never happen;
    log('exists', exists);
    const inversion = exists[1];
    log('inversion', inversion);
    if (inversion >= 0 && inversion <= 3) {
      chord.setInversion(inversion as any);
    } else {
      return false; // not a valid chord... this should never happen
    }
    return chord;
  };

  /**
   * Get a random chord based on options
   * @param {Object} options {isSeventh, quality, inversion = 0}
   * @returns {Chord}
   * If quality is specified, isSeventh is ignored
   * inversion = 0 (default), true (random)
   */
  static randomChord = (options: {
    isSeventh?: boolean;
    quality?: string;
    inversion?: boolean | InversionNumber;
  }) => {
    options = options || {};

    const isSeventh = 'isSeventh' in options ? !!options.isSeventh : !!(Math.random() < 0.5);
    let qualities = ['Maj', 'min', 'dim'];
    qualities = qualities.concat(isSeventh ? ['half-dim', 'Dom'] : ['Aug']);

    Chord.allChords(options);

    const quality =
      options.quality ||
      qualities[Math.floor(Math.random() * qualities.length)] + (isSeventh ? '7' : '');

    const allChordsOfQuality = utils.find(chordsByQuality, (el, qual) =>
      Chord.qualityEquals(quality, qual)
    );

    const [rootName, qualityName]: [ShortNoteName, ChordQualityName] =
      allChordsOfQuality[Math.floor(Math.random() * allChordsOfQuality.length)].split(' ');
    const chord = new Chord(rootName, qualityName);
    if ('inversion' in options) {
      if (options.inversion === true) {
        const inversion = Math.floor(Math.random() * chord.noteHolder.length);
        if (!Chord.isValidInversion(inversion)) {
          throw new Error(`${inversion} is not a valid inversion.`);
        }
        chord.setInversion(inversion);
      } else {
        if (!Chord.isValidInversion(options.inversion)) {
          throw new Error(`${options.inversion} is not a valid inversion.`);
        }
        chord.setInversion(options.inversion);
      }
    }
    return chord;
  };

  /* Get all the chords that exist in all keys:
   * @param isSeventh: only get sevenths or triads; get all if omitted
   * @param reduceSet: eliminate duplicates if true
   * returns array of chord names "Eb Maj7"
   */
  static allChords = (options: { reduceSet?: boolean; isSeventh?: boolean } = {}) => {
    if (!allChords.length || !chordsByQuality) generateAllChords();

    let chords = allChords;
    if (options.reduceSet) {
      const set = new Set(chords);
      chords = Array.from(set);
    }

    if ('isSeventh' in options) {
      if (options.isSeventh) {
        // get rid of triads...
        chords = chords.filter((chord) => chord.indexOf('7') !== -1);
      } else {
        // get rid of seventh chords...
        chords = chords.filter((chord) => chord.indexOf('7') === 1);
      }
    }

    return chords;
  };

  static getAllChordsByQuality() {
    generateAllChords();
    return chordsByQuality;
    //    return utils.clone(_chordsByQuality, true);
  }
}

/**
 * Initialize the private chordsByQuality and allChords variables.
 */
function generateAllChords() {
  // If we haven't already calculated all of the chords, calculate them
  // and store them in _chordsByQuality {} by quality key so we never
  // have to calculate them again:
  if (chordsByQuality) return chordsByQuality;

  // Empty allChords, too, we'll repopulate it
  allChords.length = 0;

  const chords: Record<ChordQualityName, `${ShortNoteName} ${ChordQualityName}`[]> = {
    M: [],
    M7: [],
    m: [],
    m7: [],
    o: [],
    o7: [],
    'o/7': [],
    A: [],
    7: [],
  };

  KeySignature.allKeys().forEach((key) => {
    scaleDegrees.forEach((sd) => {
      const {
        [key.quality === 'Major' ? 'major' : 'minor']: { triads, seventhChords },
      } = chordQualitiesByScaleDegree[sd];

      [...triads, ...seventhChords].forEach((quality) => {
        const note = key.getNoteAtScaleDegreeOrThrow(sd);
        chords[quality].push(`${note.toShortString()} ${quality}`);
        allChords.push(`${note.toShortString()} ${quality}`);
      });
    });
  });

  chordsByQuality = chords;
  return chords;
}
